
import { useEffect, useState, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import { Region } from '../data/types/region-types';
import { City } from '../data/types/city-types';
import { useIsMobile } from './use-mobile';
import { useToast } from './use-toast';
import { formatCurrencyShort } from '../data/utils/money-utils';

export function useMapNavigation(
  map: React.MutableRefObject<mapboxgl.Map | null>,
  mapLoaded: boolean,
  selectedRegion: Region | null | undefined,
  selectedCity: City | null | undefined,
  setMapZoomLevel: (level: 'country' | 'region' | 'city') => void,
  onChiapasEnter?: () => void,
  onHidalgoEnter?: () => void,
  wasInMexicoView?: boolean
) {
  const { isMobile } = useIsMobile();
  const { toast } = useToast();
  const [isInChiapas, setIsInChiapas] = useState(false);
  const [isInHidalgo, setIsInHidalgo] = useState(false);
  const [isGlobalView, setIsGlobalView] = useState(true);
  const toastShownRef = useRef<{chiapas: boolean, hidalgo: boolean}>({
    chiapas: false,
    hidalgo: false
  });
  const previousViewRef = useRef<'global' | 'mexico' | 'region'>('global');
  const initialLoadCompletedRef = useRef<boolean>(false);

  const showGlobalView = () => {
    if (!map.current) return;
    
    console.log('Showing global view of the world');
    
    map.current.flyTo({
      center: [0, 20],
      zoom: isMobile ? 1.8 : 2.2,
      pitch: 0,
      bearing: 0,
      duration: 1500,
      essential: true
    });
    
    setMapZoomLevel('country');
    setIsGlobalView(true);
    previousViewRef.current = 'global';
    setIsInChiapas(false);
    setIsInHidalgo(false);
  };

  const showMexicoView = () => {
    if (!map.current) return;
    
    console.log('Showing Mexico view');
    
    map.current.flyTo({
      center: isMobile ? [-101, 23] : [-102.5, 23.6],
      zoom: isMobile ? 2.4 : 4.5,
      pitch: 0,
      bearing: 0,
      duration: 1500,
      essential: true
    });
    
    setMapZoomLevel('country');
    setIsGlobalView(false);
    previousViewRef.current = 'mexico';
    setIsInChiapas(false);
    setIsInHidalgo(false);
  };

  useEffect(() => {
    if (map.current && mapLoaded && !selectedRegion && !selectedCity) {
      console.log('Map loaded, showing global view as default');
      showGlobalView();
    }
  }, [mapLoaded]);

  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    
    const chiapasCoords = [-92.2731, 16.7535];
    const hidalgoCoords = [-98.7577, 20.4859];
    const regionRadius = 1.5;
    
    const checkCurrentRegion = () => {
      if (!map.current) return;
      
      const center = map.current.getCenter();
      const zoom = map.current.getZoom();
      
      const dxChiapas = center.lng - chiapasCoords[0];
      const dyChiapas = center.lat - chiapasCoords[1];
      const distanceToChiapas = Math.sqrt(dxChiapas * dxChiapas + dyChiapas * dyChiapas);
      
      const dxHidalgo = center.lng - hidalgoCoords[0];
      const dyHidalgo = center.lat - hidalgoCoords[1];
      const distanceToHidalgo = Math.sqrt(dxHidalgo * dxHidalgo + dyHidalgo * dyHidalgo);
      
      const wasInChiapas = isInChiapas;
      const nowInChiapas = distanceToChiapas < regionRadius && zoom >= 6;
      
      const wasInHidalgo = isInHidalgo;
      const nowInHidalgo = distanceToHidalgo < regionRadius && zoom >= 6;
      
      if (!wasInChiapas && nowInChiapas) {
        console.log('Entered Chiapas region');
        setIsInChiapas(true);
        
        if (onChiapasEnter && !toastShownRef.current.chiapas) {
          onChiapasEnter();
          toastShownRef.current.chiapas = true;
        }
      } else if (wasInChiapas && !nowInChiapas) {
        console.log('Exited Chiapas region');
        setIsInChiapas(false);
      }
      
      if (!wasInHidalgo && nowInHidalgo) {
        console.log('Entered Hidalgo region');
        setIsInHidalgo(true);
        
        if (onHidalgoEnter && !toastShownRef.current.hidalgo) {
          onHidalgoEnter();
          toastShownRef.current.hidalgo = true;
        }
      } else if (wasInHidalgo && !nowInHidalgo) {
        console.log('Exited Hidalgo region');
        setIsInHidalgo(false);
      }
    };
    
    const handleZoomChange = () => {
      if (!map.current) return;
      
      const zoom = map.current.getZoom();
      
      if (zoom < 5) {
        setMapZoomLevel('country');
      } else if (zoom < 9) {
        setMapZoomLevel('region');
      } else {
        setMapZoomLevel('city');
      }
    };
    
    map.current.on('moveend', checkCurrentRegion);
    map.current.on('zoom', handleZoomChange);
    
    return () => {
      if (map.current) {
        map.current.off('moveend', checkCurrentRegion);
        map.current.off('zoom', handleZoomChange);
      }
    };
  }, [map, mapLoaded, isInChiapas, isInHidalgo, onChiapasEnter, onHidalgoEnter, setMapZoomLevel]);

  useEffect(() => {
    if (selectedRegion && map.current && mapLoaded) {
      console.log('Selected region changed:', selectedRegion.english_name);
      console.log('Region pledges:', formatCurrencyShort(selectedRegion.pledges));
      
      setIsGlobalView(false);
      previousViewRef.current = 'region';
      
      let zoom = isMobile ? 5.5 : 7;
      let pitch = 30;
      let bearing = 15;
      
      if (selectedRegion.region_id === 'CHIS') {
        zoom = isMobile ? 6.2 : 7.3;
        pitch = 20;
        
        // Ensure Chiapas specific handlers are triggered
        setTimeout(() => {
          if (onChiapasEnter) {
            console.log('Triggering Chiapas enter callback');
            onChiapasEnter();
          }
          setIsInChiapas(true);
          toastShownRef.current.chiapas = true;
        }, 100);
      } else if (selectedRegion.region_id === 'HGO') {
        // Ensure Hidalgo specific handlers are triggered
        setTimeout(() => {
          if (onHidalgoEnter) {
            console.log('Triggering Hidalgo enter callback');
            onHidalgoEnter();
          }
          setIsInHidalgo(true);
          toastShownRef.current.hidalgo = true;
        }, 100);
      }
      
      const container = map.current.getContainer();
      container.classList.add('region-view');
      container.classList.remove('mexico-view');
      
      // First set the class to ensure markers will be visible when the map move completes
      if (selectedRegion.region_id === 'CHIS' || selectedRegion.region_id === 'HGO') {
        document.body.classList.add('show-region-markers');
      }
      
      // Navigate to the region
      map.current.flyTo({
        center: selectedRegion.coordinates,
        zoom: zoom,
        pitch: pitch,
        bearing: bearing,
        duration: 1000,
        essential: true
      });
      
      setMapZoomLevel('region');
      
      // After navigation completes, force marker visibility
      map.current.once('moveend', () => {
        console.log('Region navigation complete, ensuring city markers are visible');
        
        // Force immediate visibility of markers
        setTimeout(() => {
          document.querySelectorAll('.city-marker, .mapboxgl-marker, .city-marker-funding').forEach((el) => {
            if (el instanceof HTMLElement) {
              el.style.cssText += `
                display: block !important;
                visibility: visible !important;
                opacity: 1 !important;
                z-index: 1000 !important;
                transition: none !important;
              `;
            }
          });
          
          // Remove the loading state if present
          document.body.classList.remove('map-region-loading');
        }, 200);
      });
      
      // Set state for specific regions
      if (selectedRegion.region_id === 'CHIS') {
        setIsInChiapas(true);
        setIsInHidalgo(false);
      } else if (selectedRegion.region_id === 'HGO') {
        setIsInHidalgo(true);
        setIsInChiapas(false);
      } else {
        setIsInChiapas(false);
        setIsInHidalgo(false);
      }
    } else if (!selectedRegion && !selectedCity && map.current && mapLoaded) {
      if (wasInMexicoView) {
        console.log('Returning to Mexico view based on wasInMexicoView flag');
        showMexicoView();
        previousViewRef.current = 'mexico';
      } else if (previousViewRef.current === 'global') {
        showGlobalView();
      } else {
        showGlobalView();
      }
    }
  }, [selectedRegion, mapLoaded, isMobile, setMapZoomLevel, wasInMexicoView, onChiapasEnter, onHidalgoEnter]);

  useEffect(() => {
    if (selectedCity && map.current && mapLoaded) {
      console.log('Selected city changed:', selectedCity.name);
      
      const zoom = isMobile ? 10 : 11;
      
      map.current.flyTo({
        center: selectedCity.coordinates,
        zoom: zoom,
        pitch: 45,
        bearing: 30,
        duration: 1500,
        essential: true
      });
      
      setMapZoomLevel('city');
    }
  }, [selectedCity, mapLoaded, isMobile, setMapZoomLevel]);

  useEffect(() => {
    if (!selectedRegion && !selectedCity) {
      toastShownRef.current = { chiapas: false, hidalgo: false };
    }
  }, [selectedRegion, selectedCity]);

  return { isInChiapas, isInHidalgo, isGlobalView, showGlobalView, showMexicoView };
}
